import React, {useEffect, useState} from 'react'
import {Box} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import SelectNew from 'src/components-new/inputs/SelectNew'
import TextAreaNew from 'src/components-new/inputs/TextAreaNew'
import RadioGroupNew from 'src/components-new/inputs/RadioGroupNew'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {STATUS} from 'src/constants/constants'
import {
  REMINDER_STATUSES,
  SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA,
  SecondaryTherapyDataSchemaElement,
  resolveSecondaryTherapyDataSchema,
  secondaryTherapyErrorMessage
} from 'src/constants/secondaryTherapyFields'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import StepProgress from './StepProgress'
import FrequencyStep from './FrequencyStep'
import DosageStep from './DosageStep'
import ModeOfAssumptionStep from './ModeOfAssumptionStep'
import {
  buildInitialFormState,
  buildSubmitBody,
  DosageValue,
  isFrequencyStepValid,
  SecondaryTherapyFormState,
} from './secondaryTherapyFormUtils'
import {Option} from '../../patient/tabs/therapy/therapyOptions'

// New-style equivalent of src/components/templates/SecondaryTherapyDialog/SecondaryTherapyDialog.tsx

const SUBMIT_ERROR_MESSAGE = 'Errore nel salvataggio della terapia accessoria.'

interface SecondaryTherapyDialogNewProps {
  open: boolean
  secondaryTherapy?: Record<string, any>
  onClose: () => void
  onSuccess: () => void
}

export default function SecondaryTherapyDialogNew({
  open,
  secondaryTherapy,
  onClose,
  onSuccess,
}: SecondaryTherapyDialogNewProps): JSX.Element {
  const toast = useToast()
  const isEdit = Boolean(secondaryTherapy)

  // Not tied to a specific patient's patientDetailConfiguration (this dialog lives on the
  // standalone /therapies page), so it reads therapyConfigurations.secondaryTherapiesConfiguration
  // instead, falling back to the same default catalog used everywhere else in this app.
  const configuredDataSchema: SecondaryTherapyDataSchemaElement[] | undefined = useAppSelector(
    (state) => resolveSecondaryTherapyDataSchema(state.pspConfigReducer.data.therapyConfigurations?.secondaryTherapiesConfiguration)
  )
  const dataSchema: SecondaryTherapyDataSchemaElement[] = configuredDataSchema ?? SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA
  const fieldLabel = (key: string, fallback: string): string => dataSchema.find((element) => element.value === key)?.name ?? fallback

  // "Frequenza" e "Dosaggio" restano sempre presenti: contengono campi strutturali/sempre visibili
  // ("Al bisogno", "Numero assunzioni", "Ogni", "Personalizza dosaggi") che non
  // dipendono dalla configurazione. "Modalità di assunzione" e "Note" invece possono restare senza
  // alcun campo visibile, quindi vengono tolti dal wizard in quel caso.
  const STEPS = [
    {key: 'search', title: 'Cerca farmaco'},
    {key: 'frequency', title: 'Frequenza'},
    {key: 'dosage', title: 'Dosaggio'},
    ...(dataSchema.some((element) => element.group === 'administration')
      ? [{key: 'modeOfAssumption', title: 'Modalità di assunzione'}]
      : []),
    ...(dataSchema.some((element) => element.value === 'notes')
      ? [{key: 'notes', title: fieldLabel('notes', 'Note')}]
      : []),
  ]

  const [currentStep, setCurrentStep] = useState<number>(0)
  const [formState, setFormState] = useState<SecondaryTherapyFormState>(() => buildInitialFormState(secondaryTherapy))
  const [drugOptions, setDrugOptions] = useState<Option[]>([])
  const [statusDrugs, setStatusDrugs] = useState<string>()
  const [drugError, setDrugError] = useState<boolean>(false)
  const [frequencyError, setFrequencyError] = useState<boolean>(false)
  const [statusSubmit, setStatusSubmit] = useState<string>()

  useEffect(() => {
    if (!open) return
    setCurrentStep(0)
    setDrugError(false)
    setFrequencyError(false)
    setStatusSubmit(undefined)
    setFormState(buildInitialFormState(secondaryTherapy))

    const preloadedOption: Option[] = secondaryTherapy?.drug
      ? [{value: secondaryTherapy.drug.drugId, label: secondaryTherapy.drug.name}]
      : []
    setDrugOptions(preloadedOption)

    apiClient.getDrugs('', setStatusDrugs)
      .then((drugs: Record<string, any>[] = []) => {
        // Ricerca vuota: mostriamo l'intera lista (farmaci di scope + IA) ordinata
        // alfabeticamente, per renderla più facile da scorrere.
        const sortedDrugs = [...drugs].sort((a, b) => a.name.localeCompare(b.name, 'it'))
        const fetched = sortedDrugs.map((drug) => ({value: drug._id, label: drug.name}))
        const merged = preloadedOption.length && !fetched.some((o) => o.value === preloadedOption[0].value)
          ? [...preloadedOption, ...fetched]
          : fetched
        setDrugOptions(merged)
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, secondaryTherapy])

  const dosageValue: DosageValue = {
    assumptionDosage: formState.assumptionDosage,
    infusionRate: formState.infusionRate,
    secondaryDosage: formState.secondaryDosage,
    dilution: formState.dilution,
    drugDosage: formState.drugDosage,
    dailyDosage: formState.dailyDosage,
    weeklyDosage: formState.weeklyDosage,
    monthlyDosage: formState.monthlyDosage,
    vialDosage: formState.vialDosage,
    calculationCriterion: formState.calculationCriterion,
    customDosages: formState.customDosages,
  }

  const activeStepKey = STEPS[currentStep]?.key
  const isLastStep = currentStep === STEPS.length - 1

  const validateStep = (): boolean => {
    if (activeStepKey === 'search') {
      const valid = Boolean(formState.drugId)
      setDrugError(!valid)
      return valid
    }
    if (activeStepKey === 'frequency') {
      const valid = isFrequencyStepValid(formState.frequency, dataSchema)
      setFrequencyError(!valid)
      return valid
    }
    return true
  }

  const goNext = (): void => {
    if (!validateStep()) return
    setCurrentStep((step) => step + 1)
  }

  const goBack = (): void => {
    setCurrentStep((step) => step - 1)
  }

  const handleSubmit = async (): Promise<void> => {
    if (!validateStep()) return

    const body = buildSubmitBody(formState, isEdit)

    if (isEdit) {
      // A 403 means the therapy was entered by the centre (therapy-service
      // specs/2026-10-secondary-therapy-authz.md): its own message, shown here since only onError
      // knows the status code
      let failed = false
      let errorStatusCode: number | undefined
      await apiClient.patchSecondaryTherapy(secondaryTherapy?._id, body, setStatusSubmit, (_message, statusCode) => {
        failed = true
        errorStatusCode = statusCode
      })
      if (failed) {
        toast.error(
          secondaryTherapyErrorMessage(errorStatusCode, SUBMIT_ERROR_MESSAGE),
          errorStatusCode === 403 ? {duration: 8000} : undefined
        )
      }
    } else {
      await apiClient.postSecondaryTherapy(body, setStatusSubmit)
    }
  }

  useEffect(() => {
    if (statusSubmit === STATUS.FULFILLED) {
      toast.success(isEdit ? 'Terapia accessoria aggiornata con successo' : 'Terapia accessoria aggiunta con successo')
      onSuccess()
    }
    if (statusSubmit === STATUS.REJECTED && !isEdit) {
      toast.error(SUBMIT_ERROR_MESSAGE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusSubmit])

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Modifica terapia' : 'Aggiungi terapia'}
      maxWidth="md"
      actions={(
        <>
          <Button variant="secondary" onClick={currentStep === 0 ? onClose : goBack}>
            {currentStep === 0 ? 'Annulla' : 'Indietro'}
          </Button>
          <Button
            variant="primary"
            loading={statusSubmit === STATUS.PENDING}
            onClick={isLastStep ? handleSubmit : goNext}
          >
            {isLastStep ? 'Salva' : 'Avanti'}
          </Button>
        </>
      )}
    >
      <StepProgress steps={STEPS} currentStep={currentStep} />

      <Box sx={{minHeight: 280}}>
        {activeStepKey === 'search' && (
          <>
            <SelectNew
              label="Farmaco"
              required
              showSearch
              value={formState.drugId ?? ''}
              onChange={(drugId) => setFormState((prev) => ({...prev, drugId: drugId || undefined}))}
              options={drugOptions}
              placeholder="Cerca un farmaco"
              disabled={statusDrugs === STATUS.PENDING}
              error={drugError}
              helperText={drugError ? 'Campo obbligatorio' : undefined}
            />

            {/* Only offered on create — once the therapy exists, its reminder is switched on/off
                from the list-level ReminderToggle in TherapiesNew.tsx instead. */}
            {!isEdit && (
              <Box sx={{mt: 3}}>
                <RadioGroupNew
                  label="Notifiche"
                  value={formState.reminderStatus === REMINDER_STATUSES.ACTIVE ? 'yes' : 'no'}
                  onChange={(v) => setFormState((prev) => ({
                    ...prev,
                    reminderStatus: v === 'yes' ? REMINDER_STATUSES.ACTIVE : REMINDER_STATUSES.DISABLED,
                  }))}
                  options={[{value: 'yes', label: 'Attive'}, {value: 'no', label: 'Disattivate'}]}
                />
              </Box>
            )}
          </>
        )}

        {activeStepKey === 'frequency' && (
          <>
            {frequencyError && (
              <InlineMessage severity="error" sx={{mb: 2}}>
                Numero assunzioni e frequenza (valore e unità) sono campi obbligatori.
              </InlineMessage>
            )}
            <FrequencyStep
              value={{frequency: formState.frequency, startDate: formState.startDate, endDate: formState.endDate}}
              onChange={(next) => setFormState((prev) => ({
                ...prev,
                frequency: next.frequency,
                startDate: next.startDate,
                endDate: next.endDate,
              }))}
              dataSchema={dataSchema}
            />
          </>
        )}

        {activeStepKey === 'dosage' && (
          <DosageStep
            value={dosageValue}
            onChange={(next) => setFormState((prev) => ({...prev, ...next}))}
            dataSchema={dataSchema}
          />
        )}

        {activeStepKey === 'modeOfAssumption' && (
          <ModeOfAssumptionStep
            value={{
              modeOfAssumption: formState.modeOfAssumption,
              deviceOfAssumption: formState.deviceOfAssumption,
              speedOfDevice: formState.speedOfDevice,
            }}
            onChange={(next) => setFormState((prev) => ({...prev, ...next}))}
            dataSchema={dataSchema}
          />
        )}

        {activeStepKey === 'notes' && (
          <TextAreaNew
            label={fieldLabel('notes', 'Note')}
            fullWidth
            rows={4}
            value={formState.notes}
            onChange={(notes) => setFormState((prev) => ({...prev, notes}))}
          />
        )}
      </Box>
    </AppDialog>
  )
}
